import * as THREE from "three";
import type { CowryGeometryParams } from "../core/types";

const BODY_SEGMENTS = 128;
const RING_SEGMENTS = 72;

export interface CowryGeometryParts {
  root: THREE.Group;
  body: THREE.Mesh<THREE.BufferGeometry, THREE.Material>;
}

export interface CowryGeometryBuildOptions {
  ventralFoldEmphasis?: number;
}

export function buildCowryGeometry(
  params: CowryGeometryParams,
  options: CowryGeometryBuildOptions = {},
): CowryGeometryParts {
  const ventralFoldEmphasis = options.ventralFoldEmphasis ?? 1;
  if (!Number.isFinite(ventralFoldEmphasis) || ventralFoldEmphasis <= 0) {
    throw new RangeError("Ventral fold emphasis must be finite and greater than zero.");
  }
  const root = new THREE.Group();
  root.name = "cowry-shell";

  const ivory = new THREE.MeshPhysicalMaterial({
    name: "milestone-one-ivory",
    color: 0xeee4ce,
    roughness: 0.23,
    metalness: 0,
    clearcoat: 0.48,
    clearcoatRoughness: 0.2,
  });

  const body = new THREE.Mesh(createBodySurface(params, ventralFoldEmphasis), ivory);
  body.name = "shell-body";
  body.castShadow = true;
  body.receiveShadow = true;
  root.add(body);

  return { root, body };
}

function createBodySurface(
  params: CowryGeometryParams,
  ventralFoldEmphasis: number,
): THREE.BufferGeometry {
  const positions: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];

  for (let ix = 0; ix <= BODY_SEGMENTS; ix += 1) {
    const longitudinal = ix / BODY_SEGMENTS;
    const xUnit = longitudinal * 2 - 1;
    const taperBase = Math.max(0, 1 - Math.pow(Math.abs(xUnit), 2.35));
    const taper = Math.pow(taperBase, params.endTaper);
    const endLift = 0.11 * Math.pow(Math.abs(xUnit), 2.4);
    const organicBias = 1 + params.longitudinalAsymmetry * xUnit;

    for (let ir = 0; ir <= RING_SEGMENTS; ir += 1) {
      const around = ir / RING_SEGMENTS;
      const angle = ir === RING_SEGMENTS ? 0 : around * Math.PI * 2;
      const sin = Math.sin(angle);
      const cos = Math.cos(angle);
      const isDorsal = sin >= 0;
      const verticalScale = isDorsal
        ? 1 + params.dorsalBulge * Math.pow(sin, 2)
        : params.ventralFlatness;
      const z = cos * params.shellWidth * 0.5 * taper * organicBias;
      let y = sin * params.shellHeight * 0.5 * taper * verticalScale + endLift;

      if (!isDorsal) y += ventralRecessLift(
        xUnit,
        z,
        taperBase,
        params,
        ventralFoldEmphasis,
      );

      const x = xUnit * params.shellLength * 0.5;
      positions.push(x, y, z);
      uvs.push(longitudinal, around);
    }
  }

  const stride = RING_SEGMENTS + 1;
  for (let ix = 0; ix < BODY_SEGMENTS; ix += 1) {
    for (let ir = 0; ir < RING_SEGMENTS; ir += 1) {
      const a = ix * stride + ir;
      const b = (ix + 1) * stride + ir;
      const c = b + 1;
      const d = a + 1;
      indices.push(a, b, d, b, c, d);
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute("uv", new THREE.Float32BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}

function ventralRecessLift(
  xUnit: number,
  z: number,
  taperBase: number,
  params: CowryGeometryParams,
  emphasis: number,
): number {
  const foldCenter = params.ventralFoldCurve * Math.sin(Math.PI * xUnit);
  const recess = Math.exp(-Math.pow((z - foldCenter) / params.ventralRecessWidth, 2));
  const endFade = Math.pow(taperBase, 0.55);
  return recess * params.shellHeight * 0.1 * emphasis * endFade;
}
